/**
 * L4-JUICE-004: Hit Stop Manager
 *
 * Manages brief animation freezes when attacks connect.
 * Adds weight and impact to combat actions.
 */

import type { CombatGridRenderer } from '../components/CombatGrid/CombatGridRenderer';

/**
 * Hit stop intensity levels
 */
export type HitStopIntensity = 'light' | 'medium' | 'heavy';

/**
 * Hit stop configuration
 */
interface HitStopConfig {
  duration: number;        // Milliseconds
  damageThreshold: number; // Minimum damage for this intensity
}

/**
 * Active hit stop tracking
 */
interface ActiveHitStop {
  unitIds: string[];
  resumeTime: number;
  timeoutId: number;
}

/**
 * HitStopManager
 *
 * Manages both per-unit animation freezes (sprite .stop()/.play())
 * and global state-update pauses on damage impact.
 */
export class HitStopManager {
  private gridRenderer: CombatGridRenderer;
  private activeHitStops: ActiveHitStop[] = [];

  // Global state pause (formerly HitstopManager)
  private isStatePaused: boolean = false;
  private statePauseEndTime: number = 0;
  private static readonly STATE_PAUSE_DEFAULT_MS = 150;
  private static readonly STATE_PAUSE_HEAVY_MS = 250;
  private static readonly STATE_PAUSE_CRITICAL_MS = 350;

  private static readonly CONFIG: Record<HitStopIntensity, HitStopConfig> = {
    light: { duration: 50, damageThreshold: 0 },    // 3 frames @ 60fps
    medium: { duration: 83, damageThreshold: 10 },  // 5 frames @ 60fps
    heavy: { duration: 167, damageThreshold: 30 }   // 10 frames @ 60fps
  };

  constructor(gridRenderer: CombatGridRenderer) {
    this.gridRenderer = gridRenderer;
  }

  /**
   * Trigger hit stop effect
   * Freezes attacker and victim animations briefly
   *
   * @param attackerId - Attacking unit ID
   * @param victimId - Victim unit ID
   * @param intensity - Hit stop intensity
   */
  public trigger(
    attackerId: string,
    victimId: string,
    intensity: HitStopIntensity
  ): void {
    const config = HitStopManager.CONFIG[intensity];
    const unitIds = [attackerId, victimId];

    // Freeze animations
    this.freezeAnimations(unitIds);

    // Schedule resume
    const resumeTime = Date.now() + config.duration;
    const timeoutId = window.setTimeout(() => {
      this.resumeAnimations(unitIds);
      this.removeActiveHitStop(timeoutId);
    }, config.duration);

    // Track active hit stop
    this.activeHitStops.push({ unitIds, resumeTime, timeoutId });
  }

  /**
   * Calculate hit stop intensity based on damage amount
   *
   * @param damage - Damage dealt
   * @returns Appropriate intensity level
   */
  public calculateIntensity(damage: number): HitStopIntensity {
    if (damage >= HitStopManager.CONFIG.heavy.damageThreshold) {
      return 'heavy';
    }
    if (damage >= HitStopManager.CONFIG.medium.damageThreshold) {
      return 'medium';
    }
    return 'light';
  }

  /**
   * Get configuration for intensity level
   * (Exposed for testing)
   */
  public getConfig(intensity: HitStopIntensity): HitStopConfig {
    return HitStopManager.CONFIG[intensity];
  }

  /**
   * Clear all active hit stops
   * Used for cleanup on combat end
   */
  public clearAll(): void {
    for (const hitStop of this.activeHitStops) {
      clearTimeout(hitStop.timeoutId);
      this.resumeAnimations(hitStop.unitIds);
    }
    this.activeHitStops = [];
  }

  /**
   * Get count of active hit stops
   * (Exposed for testing/debugging)
   */
  public getActiveCount(): number {
    return this.activeHitStops.length;
  }

  /**
   * Freeze sprite animations for specified units
   */
  private freezeAnimations(unitIds: string[]): void {
    for (const unitId of unitIds) {
      const spriteData = this.gridRenderer.getSpriteByUnitId(unitId);
      if (spriteData?.sprite && 'stop' in spriteData.sprite) {
        (spriteData.sprite as any).stop();
      }
    }
  }

  /**
   * Resume sprite animations for specified units
   */
  private resumeAnimations(unitIds: string[]): void {
    for (const unitId of unitIds) {
      const spriteData = this.gridRenderer.getSpriteByUnitId(unitId);
      if (spriteData?.sprite && 'play' in spriteData.sprite) {
        (spriteData.sprite as any).play();
      }
    }
  }

  /**
   * Remove completed hit stop from tracking
   */
  private removeActiveHitStop(timeoutId: number): void {
    this.activeHitStops = this.activeHitStops.filter(
      (hs) => hs.timeoutId !== timeoutId
    );
  }

  /**
   * Trigger a global state-update pause for dramatic emphasis.
   * During pause, rendering continues but state updates are skipped.
   * Only extends existing pauses, never shortens.
   */
  public triggerStatePause(damage?: number): void {
    let duration = HitStopManager.STATE_PAUSE_DEFAULT_MS;
    if (damage !== undefined) {
      if (damage > 100) {
        duration = HitStopManager.STATE_PAUSE_CRITICAL_MS;
      } else if (damage > 50) {
        duration = HitStopManager.STATE_PAUSE_HEAVY_MS;
      }
    }
    const newEndTime = Date.now() + duration;
    if (newEndTime > this.statePauseEndTime) {
      this.isStatePaused = true;
      this.statePauseEndTime = newEndTime;
    }
  }

  /**
   * Check if currently in a global state pause.
   * Auto-resumes when time expires.
   */
  public isInStatePause(): boolean {
    if (this.isStatePaused && Date.now() >= this.statePauseEndTime) {
      this.isStatePaused = false;
    }
    return this.isStatePaused;
  }

  /**
   * Force end the global state pause immediately.
   */
  public endStatePause(): void {
    this.isStatePaused = false;
    this.statePauseEndTime = 0;
  }

  /**
   * Get remaining state pause time in ms.
   */
  public getStatePauseRemaining(): number {
    if (!this.isStatePaused) return 0;
    return Math.max(0, this.statePauseEndTime - Date.now());
  }

  /**
   * Destroy manager and cleanup
   */
  public destroy(): void {
    this.clearAll();
    this.endStatePause();
  }
}
